import { supabase } from './supabase';

export type ImportMissingResponse = {
  inserted?: number;
  recipients_total?: number;
  newsletter_id?: string;
  import_missing_only?: boolean;
  subscriber_id?: string;
  error?: string;
  code?: string;
};

function isImportMissingBody(body: Record<string, unknown>): boolean {
  return (
    body.import_missing_only === true ||
    body.import_missing_only === 'true' ||
    body.mode === 'import_missing'
  );
}

/**
 * Invoke materialize-newsletter. On non-2xx, surfaces the edge fn `{ error }`
 * payload (supabase-js often leaves `data` empty for failed invokes).
 */
export async function invokeMaterializeNewsletter(
  body: Record<string, unknown>,
): Promise<ImportMissingResponse> {
  const payload = isImportMissingBody(body)
    ? { ...body, import_missing_only: true, mode: 'import_missing' }
    : body;

  const { data, error } = await supabase.functions.invoke('materialize-newsletter', {
    body: payload,
  });

  if (error) {
    let detail: string | null = null;
    const ctx = (error as { context?: unknown }).context;
    if (ctx && typeof (ctx as Response).json === 'function') {
      try {
        const parsed = (await (ctx as Response).clone().json()) as { error?: string };
        if (parsed?.error) detail = parsed.error;
      } catch {
        try {
          const text = await (ctx as Response).clone().text();
          if (text) {
            try {
              const parsed = JSON.parse(text) as { error?: string };
              detail = parsed.error ?? text;
            } catch {
              detail = text;
            }
          }
        } catch {
          /* ignore */
        }
      }
    }
    if (!detail) {
      detail = (data as ImportMissingResponse | null)?.error ?? null;
    }
    throw new Error(detail || error.message);
  }

  const result = (data ?? {}) as ImportMissingResponse;
  if (result.error) {
    throw new Error(result.error);
  }
  return result;
}

export async function importNewsletterMissingRecipients(
  newsletterId: string,
  subscriberId?: string,
): Promise<number> {
  const body: Record<string, unknown> = {
    newsletter_id: newsletterId,
    import_missing_only: true,
    mode: 'import_missing',
  };
  if (subscriberId) {
    body.subscriber_id = subscriberId;
  }

  const result = await invokeMaterializeNewsletter(body);
  return typeof result.inserted === 'number' ? result.inserted : 0;
}
